<template>
  <div class="lookup-selector mt-3">
    <ComboboxBase
      ref="lookupSelectorCombobox"
      class="lookup-selector__combobox"
      :components="{ OpenIndicator: '' }"
      placeholder="Start typing to search."
      :options="items"
      :searchable="true"
      name="keywords"
      :default-style-disabled="true"
      no-options-label="No results."
      @updateInput="onAddChip"
      @search="updateSearch"
    ></ComboboxBase>
    <ul class="no-bullet inline-list">
      <li v-for="(chip, indexNo) in selected" :key="indexNo">
        <v-chip class="mt-2 mr-2">
          {{ chip }}
          <button @click.prevent="onRemoveChip(indexNo)">
            <v-icon right style="font-size: 18px">{{ mdiCloseCircle }}</v-icon>
            <span class="d-sr-only">remove {{ ariaLabel }} {{ chip }}</span>
          </button>
        </v-chip>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapState } from "vuex";

import { mdiCloseCircle } from "@mdi/js";
import debounce from "~/store/manage/utilities/debounce";
import runGraphQLQuery from "~/store/manage/utilities/runGraphQLQuery";
import ComboboxBase from "~/components/combobox/ComboboxBase.vue";

const getResultsWithDelay = debounce(async function getResultsWithDelay(val) {
  this.isLoading = true;

  const params = { searchTerm: val };

  await runGraphQLQuery(this.source, params, this.$axios)
    .then((data) => {
      this.items = data.results;
      this.items.forEach((item) => {
        const name = item[this.sourceName];
        const description = item[this.sourceDescription];
        let displayName = name;

        if (description && displayName) {
          const lowerCaseDisplayName = displayName.toLowerCase();
          const lowerCaseDescription = description.toLowerCase();

          if (!lowerCaseDisplayName.includes(lowerCaseDescription)) {
            displayName = displayName + " - " + description;
          }
        }

        item.displayName = displayName;
      });
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    })
    .finally(() => (this.isLoading = false));
}, 500);

export default {
  name: "LookupSelector",
  components: {
    ComboboxBase,
  },
  props: {
    selected: { type: Array, default: () => [] },
    ariaLabel: { type: String, default: "item" },
    source: { type: Object, required: true },
  },
  data: () => {
    return {
      items: [],
      isLoading: false,
      search: null,
      // Icons
      mdiCloseCircle,
    };
  },
  computed: {
    ...mapState({
      layout: (state) => state.theme.layout,
    }),
  },
  watch: {
    search(val) {
      if (!val) {
        this.items = [];

        return;
      }

      getResultsWithDelay.call(this, val);
    },
  },
  methods: {
    updateSearch(value) {
      this.search = value;
    },

    clearInput() {
      this.items = [];
      this.$refs.lookupSelectorCombobox.clearEntry();
    },
    onAddChip(newItem) {
      const { name: newChip } = newItem || {};

      if (!newChip?.length) {
        return;
      }

      let oldChips = this.selected ? [...this.selected] : [];

      if (newChip.includes(",")) {
        oldChips = [...oldChips, ...newChip.split(",")];
      } else if (newChip.includes(";")) {
        oldChips = [...oldChips, ...newChip.split(";")];
      } else {
        oldChips.push(newChip);
      }

      this.newChips = oldChips.filter((chip) => !!chip);
      this.newChip = "";

      this.$emit("selectionUpdated", this.newChips);
      this.clearInput();
    },
    onRemoveChip(index) {
      const newChips = this.selected ? [...this.selected] : [];

      if (index < newChips.length) {
        newChips.splice(index, 1);

        this.newChips = newChips;
      }

      this.$emit("selectionUpdated", this.newChips);
    },
  },
};
</script>
<style lang="scss" scoped>
.lookup-selector__combobox {
  --vs-search-input-bg: none;
}

.lookup-selector__combobox::v-deep {
  input {
    position: absolute;
    width: 100%;
  }

  .vs__dropdown-toggle {
    border-width: 0 0 thin 0;
    border-style: solid;
    border-radius: 0;
    &:hover {
      border-color: rgba(0, 0, 0, 0.42);
      transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    }
  }
  .vs__dropdown-menu {
    padding-left: 0;
    top: 30px;
  }
  .vs__dropdown-option {
    white-space: normal;
  }
  .vs__dropdown-option--highlight {
    --vs-dropdown-option--active-bg: var(--v-hardened-base);
  }
  .vs__selected-options {
    min-height: 30px;
  }
  .vs__search {
    cursor: text;
  }
}
</style>
